/**
 * @file Inline SVG icons for course-kit components.
 * @module @labcraft/course-kit/components/icons
 *
 * Inline so the SVG inherits `currentColor` and the bundle stays small
 * (no extra HTTP request, no icon font). Each icon is a thin wrapper
 * around the path data; consumers control size via `width`/`height`
 * props or CSS, and color via `color` on a parent.
 *
 * Subset of the platform's icon library — only icons used by
 * course-kit components live here. Platforms keep their own broader
 * sets.
 */

import type { JSX, SVGProps } from 'react'

type IconProps = SVGProps<SVGSVGElement>

export function CheckIcon(props: IconProps): JSX.Element {
  return (
    <svg
      viewBox="0 0 14 14"
      width={14}
      height={14}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}>
      <polyline points="3,7 6,10 11,4" />
    </svg>
  )
}

export function PlayTriangleIcon(props: IconProps): JSX.Element {
  return (
    <svg
      viewBox="0 0 11 11"
      width={11}
      height={11}
      fill="currentColor"
      stroke="none"
      aria-hidden
      {...props}>
      <polygon points="2.5,1.5 9.5,5.5 2.5,9.5" />
    </svg>
  )
}

export function LinesIcon(props: IconProps): JSX.Element {
  return (
    <svg
      viewBox="0 0 12 12"
      width={12}
      height={12}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      aria-hidden
      {...props}>
      <line x1={2.5} y1={3.5} x2={9.5} y2={3.5} />
      <line x1={2.5} y1={6} x2={9.5} y2={6} />
      <line x1={2.5} y1={8.5} x2={7} y2={8.5} />
    </svg>
  )
}

export function InfoCircleIcon(props: IconProps): JSX.Element {
  return (
    <svg
      viewBox="0 0 14 14"
      width={14}
      height={14}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}>
      <circle cx={7} cy={7} r={5.5} />
      <line x1={7} y1={6} x2={7} y2={10} />
      <circle cx={7} cy={4.2} r={0.4} fill="currentColor" />
    </svg>
  )
}

export function LightbulbIcon(props: IconProps): JSX.Element {
  return (
    <svg
      viewBox="0 0 14 14"
      width={14}
      height={14}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}>
      <path d="M4.5 9.5a4 4 0 1 1 5 0c-.6.5-.9 1-.9 1.6H5.4c0-.6-.3-1.1-.9-1.6Z" />
      <line x1={5.6} y1={12.8} x2={8.4} y2={12.8} />
    </svg>
  )
}

export function ReportIcon(props: IconProps): JSX.Element {
  return (
    <svg
      viewBox="0 0 14 14"
      width={14}
      height={14}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}>
      <path d="M2 3.2A1.2 1.2 0 0 1 3.2 2h7.6A1.2 1.2 0 0 1 12 3.2v5.6A1.2 1.2 0 0 1 10.8 10H7.5L4.5 12.5V10H3.2A1.2 1.2 0 0 1 2 8.8Z" />
      <line x1={7} y1={4.5} x2={7} y2={6.5} />
      <circle cx={7} cy={8.2} r={0.4} fill="currentColor" />
    </svg>
  )
}

export function AlertTriangleIcon(props: IconProps): JSX.Element {
  return (
    <svg
      viewBox="0 0 14 14"
      width={14}
      height={14}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}>
      <path d="M7 2.2 12.6 11.8H1.4Z" />
      <line x1={7} y1={6} x2={7} y2={8.4} />
      <circle cx={7} cy={10.2} r={0.4} fill="currentColor" />
    </svg>
  )
}

export function OctagonAlertIcon(props: IconProps): JSX.Element {
  return (
    <svg
      viewBox="0 0 14 14"
      width={14}
      height={14}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}>
      <path d="M4.7 1.5h4.6l3.2 3.2v4.6l-3.2 3.2H4.7l-3.2-3.2V4.7Z" />
      <line x1={7} y1={4.5} x2={7} y2={7.5} />
      <circle cx={7} cy={9.6} r={0.4} fill="currentColor" />
    </svg>
  )
}

export function HistoryIcon(props: IconProps): JSX.Element {
  return (
    <svg
      viewBox="0 0 14 14"
      width={14}
      height={14}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}>
      <path d="M2.2 7a4.8 4.8 0 1 0 1.4-3.4" />
      <polyline points="2.2,1.8 2.2,4.2 4.6,4.2" />
      <polyline points="7,4.5 7,7 9,8.2" />
    </svg>
  )
}

export function CopyIcon(props: IconProps): JSX.Element {
  return (
    <svg
      viewBox="0 0 14 14"
      width={13}
      height={13}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}>
      <rect x={4.5} y={4.5} width={7.5} height={7.5} rx={1.3} />
      <path d="M9.5 4.5V3.2A1.2 1.2 0 0 0 8.3 2H3.2A1.2 1.2 0 0 0 2 3.2v5.1A1.2 1.2 0 0 0 3.2 9.5h1.3" />
    </svg>
  )
}

export function ChevronRightIcon(props: IconProps): JSX.Element {
  return (
    <svg
      viewBox="0 0 8 12"
      width={8}
      height={12}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}>
      <polyline points="2,2 6,6 2,10" />
    </svg>
  )
}

/**
 * Padlock — marks a gated affordance (the chapter footer's next button
 * while the chapter's exercises are unpassed). Body rect + shackle arc.
 */
export function LockIcon(props: IconProps): JSX.Element {
  return (
    <svg
      viewBox="0 0 14 14"
      width={14}
      height={14}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}>
      <rect x={3} y={6.5} width={8} height={5.5} rx={1.2} />
      <path d="M4.75 6.5V5a2.25 2.25 0 0 1 4.5 0v1.5" />
    </svg>
  )
}
